import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { LiveKitRoom, RoomAudioRenderer, useRoomContext, useRemoteParticipants } from '@livekit/components-react';
import { RoomEvent, RemoteParticipant, Track } from 'livekit-client';
import { getAccessToken, getLivestreamSession, joinLivestream, registerEventAttendanceLivestream } from './api';
import { ViewerBreakoutRoomBanner } from './components/BreakoutRooms/ViewerBreakoutRoomBanner';
import { ViewerConference } from './ViewerConference';
import { Play, CheckCircle } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getConfigOrThrow,
  makeResetCache,
  notify,
  selectUser,
  useCacheQuery,
  useCacheRecord,
} from 'gossamer-front-end';
import { BookingExtendedView, LivestreamSession } from 'cb-model';
import { hasStartedViewerSession, isHostParticipantMetadata, isStartedLivestreamStatus } from './viewerLivestreamState';
import { formatEventUrl } from 'shared-front-end';
import classes from './ViewerConference.module.css';

type AutoAttendanceState = 'idle' | 'submitting' | 'success' | 'error';

interface RouteParams extends Record<string, string | undefined> {
  roomId?: string;
}

interface LocationState {
  participantName?: string;
}

function isHostParticipant(participant: RemoteParticipant) {
  return isHostParticipantMetadata(participant.metadata);
}

function participantHasLiveMedia(participant: RemoteParticipant) {
  return Array.from(participant.trackPublications.values()).some((publication) => {
    return [Track.Source.Camera, Track.Source.Microphone, Track.Source.ScreenShare].includes(publication.source);
  });
}

function StreamDetector({
  onStreamStart,
  onStreamStop,
  sessionStatus,
}: {
  onStreamStart: () => void;
  onStreamStop: () => void;
  sessionStatus?: string | null;
}): null {
  const room = useRoomContext();
  const participants = useRemoteParticipants();

  const detectStartedStream = useCallback(() => {
    if (!room) {
      return;
    }

    const hostHasLiveMedia = Array.from(room.remoteParticipants.values()).some((participant) => {
      return isHostParticipant(participant) && participantHasLiveMedia(participant);
    });

    if (hostHasLiveMedia || hasStartedViewerSession(sessionStatus, room.remoteParticipants.values())) {
      onStreamStart();
      return;
    }

    onStreamStop();
  }, [onStreamStart, onStreamStop, room, sessionStatus]);

  useEffect(() => {
    detectStartedStream();
  }, [detectStartedStream, participants]);

  useEffect(() => {
    if (!room) return;

    const handleRoomStateChange = () => detectStartedStream();

    handleRoomStateChange();
    room.on(RoomEvent.ParticipantConnected, handleRoomStateChange);
    room.on(RoomEvent.ParticipantDisconnected, handleRoomStateChange);
    room.on(RoomEvent.TrackPublished, handleRoomStateChange);
    room.on(RoomEvent.TrackSubscribed, handleRoomStateChange);
    room.on(RoomEvent.TrackUnpublished, handleRoomStateChange);
    room.on(RoomEvent.TrackUnsubscribed, handleRoomStateChange);

    return () => {
      room.off(RoomEvent.ParticipantConnected, handleRoomStateChange);
      room.off(RoomEvent.ParticipantDisconnected, handleRoomStateChange);
      room.off(RoomEvent.TrackPublished, handleRoomStateChange);
      room.off(RoomEvent.TrackSubscribed, handleRoomStateChange);
      room.off(RoomEvent.TrackUnpublished, handleRoomStateChange);
      room.off(RoomEvent.TrackUnsubscribed, handleRoomStateChange);
    };
  }, [detectStartedStream, room]);

  useEffect(() => {
    if (!room) {
      return;
    }

    const interval = setInterval(() => {
      detectStartedStream();
    }, 1000);

    return () => clearInterval(interval);
  }, [detectStartedStream, room]);

  return null;
}

export default function ViewerLivestream() {
  const { roomId } = useParams<RouteParams>();
  const location = useLocation() as { state?: LocationState };
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const dispatch = useDispatch();
  const participantName = location.state?.participantName || `Anonymous-${Math.floor(Math.random() * 1000)}`;

  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [isJoined, setIsJoined] = useState(false);
  const [isWaitingForStart, setIsWaitingForStart] = useState(false);
  const [streamStarted, setStreamStarted] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [isRoomDeleted, setIsRoomDeleted] = useState<boolean>(false);
  const [autoAttendanceState, setAutoAttendanceState] = useState<AutoAttendanceState>('idle');
  const [autoAttendanceError, setAutoAttendanceError] = useState('');
  const autoAttendanceAttempted = useRef(false);

  const livestreamSessionRecord = useCacheRecord(LivestreamSession, roomId || '', !roomId);
  const eventId = livestreamSessionRecord.data?.event_id;
  const bookingQuery = useCacheQuery(
    BookingExtendedView,
    {
      where: {
        and: [
          { fieldId: 'event_id', operator: 'eq', value: eventId },
          { fieldId: 'user_id', operator: 'eq', value: user?.userId },
          { fieldId: 'booking_status', operator: 'eq', value: 'active' },
        ],
      },
      limit: 1,
    },
    !eventId || !user?.userId,
  );
  const booking = bookingQuery.data?.[0];
  const shouldAutoRecordAttendance = booking?.flow_option !== 'none' && !booking?.attendance_id;
  const showFeedbackButton = booking?.flow_option === 'full';
  const livestreamStarted = isStartedLivestreamStatus(livestreamSessionRecord.data?.status);

  useEffect(() => {
    if (!isRoomDeleted || autoAttendanceAttempted.current || !booking) {
      return;
    }

    autoAttendanceAttempted.current = true;

    if (!shouldAutoRecordAttendance) {
      setAutoAttendanceState('success');
      return;
    }

    setAutoAttendanceState('submitting');
    registerEventAttendanceLivestream(booking.event_id)
      .then((response) => {
        const alreadyRecorded = (response.error || '').includes('already recorded');
        if (response.status < 300 || alreadyRecorded) {
          setAutoAttendanceState('success');
          makeResetCache(dispatch)('BookingExtendedView', String(booking.ticket_booking_id));
          return;
        }
        setAutoAttendanceState('error');
        setAutoAttendanceError(response.error || 'Could not automatically record attendance.');
      })
      .catch((error) => {
        console.error('Livestream attendance submission failed:', error);
        setAutoAttendanceState('error');
        setAutoAttendanceError('Could not automatically record attendance.');
      });
  }, [booking, dispatch, isRoomDeleted, shouldAutoRecordAttendance]);

  const isRoomNotStartedError = (err: any) => {
    return err.response?.data?.error === 'Room does not exist. Only a host can create a new room';
  };

  async function refreshLivestreamStartedState() {
    if (!roomId) {
      return false;
    }

    try {
      const [response, responseStatusCode] = await getLivestreamSession(roomId);
      if (responseStatusCode === 200) {
        return isStartedLivestreamStatus(response?.status);
      }
    } catch (error) {
      console.error('Failed to refresh livestream state:', error);
    }

    return false;
  }

  async function getViewerToken() {
    if (!roomId) return;

    const [response, responseStatusCode] = await getAccessToken(roomId, 'viewer');

    if (responseStatusCode !== 200 || !response?.token) {
      const error = new Error(response?.error || 'Failed to get livestream token') as Error & {
        response?: { data?: { error?: string }; status?: number };
      };
      error.response = {
        status: responseStatusCode,
        data: {
          error: response?.error || 'Failed to get livestream token',
        },
      };
      throw error;
    }

    setToken(response.token);
  }

  async function connectViewer(recordJoin: boolean) {
    if (!roomId) return;

    if (recordJoin) {
      const [response, responseStatusCode] = await joinLivestream(roomId);
      if (responseStatusCode !== 200) {
        const responseError = (response as { error?: string } | undefined)?.error;
        const error = new Error(responseError || 'Error joining livestream') as Error & {
          response?: { data?: { error?: string }; status?: number };
        };
        error.response = {
          status: responseStatusCode,
          data: {
            error: responseError || 'Error joining livestream',
          },
        };
        throw error;
      }
    }

    await getViewerToken();
    setStreamStarted(await refreshLivestreamStartedState());
    setError('');
    setIsJoined(true);
    setIsWaitingForStart(false);
    setIsRoomDeleted(false);
  }

  const requestPermissions = async () => {
    try {
      // Request both audio and video permissions
      await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      notify(['Permissions for viewing the livestream granted.', 'success']);
    } catch (error) {
      console.error('Audio permission denied:', error);
      notify(['You need to grant permissions to view the livestream! Refresh your browser and try again!', 'danger']);
    }
  };

  useEffect(() => {
    requestPermissions();
  }, []);

  useEffect(() => {
    if (!participantName || !roomId) {
      notify(['Error joining livestream', 'danger']);
      return;
    }

    (async () => {
      try {
        await connectViewer(true);
      } catch (err: any) {
        console.error('Error joining room:', err);

        if (isRoomNotStartedError(err)) {
          setIsWaitingForStart(true);
          setError('');
          return;
        }

        setError(err.response?.data?.error || 'Failed to join room.');
      }
    })();
  }, []);

  useEffect(() => {
    if (!isWaitingForStart || token || isJoined || !roomId) {
      return;
    }

    const interval = setInterval(async () => {
      try {
        await getViewerToken();
        setIsJoined(true);
        setIsWaitingForStart(false);
        setIsRoomDeleted(false);
      } catch (err: any) {
        if (isRoomNotStartedError(err)) {
          return;
        }

        console.error('Failed while waiting for livestream start:', err);
        setIsWaitingForStart(false);
        setError(err.response?.data?.error || 'Failed to join room.');
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [isJoined, isWaitingForStart, roomId, token]);

  function handleDisconnection(reason?: any) {
    setStreamStarted(false);
    // ROOM_DELETED
    if (reason === 5 || reason === 'ROOM_DELETED') {
      setIsRoomDeleted(true);
      setIsWaitingForStart(false);
      notify(['The livestream has ended.', 'info']);
      setIsJoined(false);
      setToken('');
    } else {
      if (reason !== 'CLIENT_INITIATED' && reason !== 1 && roomId) {
        // Only reconnect if it wasn't user-initiated
        setIsReconnecting(true);
        setTimeout(() => {
          reconnectToRoom();
        }, 2000);
      } else {
        setIsWaitingForStart(false);
        setIsJoined(false);
        setToken('');
        navigate('/');
      }
    }
  }

  async function reconnectToRoom() {
    if (!roomId) return;

    try {
      await getViewerToken();
      setStreamStarted(await refreshLivestreamStartedState());
      setIsReconnecting(false);
      setIsWaitingForStart(false);
    } catch (err: any) {
      console.error('Reconnection failed:', err);

      if (isRoomNotStartedError(err)) {
        setIsRoomDeleted(true);
        setIsJoined(false);
        setToken('');
        setIsReconnecting(false);
        return;
      }

      setTimeout(() => reconnectToRoom(), 3000);
    }
  }

  function handleStreamStart() {
    setStreamStarted(true);
  }

  function handleStreamStop() {
    setStreamStarted(false);
  }

  if (error) {
    return <div style={{ color: 'red' }}>{error}</div>;
  }

  if (!isReconnecting && !isJoined && !token && isRoomDeleted) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <div className="text-center">
          <div className="mb-4">
            <div className="w-16 h-16 bg-blue-500 rounded-full mx-auto mb-4 flex items-center justify-center relative">
              <Play fill="white" color="white" className="w-8 h-8" />
              <CheckCircle
                color="white"
                className="w-6 h-6 right-[-4px] absolute bottom-0 bg-green-500 p-[2px] rounded-full"
              />
            </div>
          </div>
          <h3 className="text-xl font-semibold text-gray-700 mb-2">The livestream has ended.</h3>
          <p className="text-gray-500">Thank you for watching!</p>
          {booking && (
            <div className="mt-6 flex flex-col items-center gap-3">
              {shouldAutoRecordAttendance && autoAttendanceState === 'submitting' && (
                <p className="text-sm text-gray-500">Recording your attendance...</p>
              )}
              {shouldAutoRecordAttendance && autoAttendanceState === 'success' && (
                <p className="text-sm text-green-700">Your attendance has been recorded.</p>
              )}
              {shouldAutoRecordAttendance && autoAttendanceState === 'error' && (
                <p className="max-w-md text-sm text-red-600">{autoAttendanceError}</p>
              )}
              {showFeedbackButton && (
                <>
                  <button
                    onClick={() => navigate(`/website/event-feedback/${booking.event_id}`)}
                    className="py-[0.5rem] px-[1.25rem] rounded-[2rem] mt-2 bg-blue-500 text-white font-semibold cursor-pointer border-none"
                  >
                    Leave feedback
                  </button>
                  to get access to your certificate of attendance
                </>
              )}
              <button
                onClick={() => navigate(formatEventUrl(booking.title, booking.event_id))}
                className="py-2 px-6 rounded-lg bg-primary text-white font-semibold cursor-pointer border-none"
              >
                Return to event
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (isWaitingForStart && !isJoined && !token) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <div className="text-center">
          <div className="animate-pulse mb-4">
            <div className="w-16 h-16 bg-blue-500 rounded-full mx-auto mb-4"></div>
          </div>
          <h3 className="text-xl font-semibold text-gray-700 mb-2">Waiting for livestream to start...</h3>
          <p className="text-gray-500">The host hasn't started streaming yet. You'll join automatically.</p>
        </div>
      </div>
    );
  }

  if (!isJoined) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <div className="text-center">
          <div className="animate-pulse mb-4">
            <div className="w-16 h-16 bg-blue-500 rounded-full mx-auto mb-4"></div>
          </div>
          <h3 className="text-xl font-semibold text-gray-700 mb-2">Connecting as viewer...</h3>
        </div>
      </div>
    );
  }

  return (
    <main className="livestream-container min-h-screen bg-white p-4">
      <div className="livekit-container mx-auto w-full max-w-screen-2xl mt-16 mb-24">
        <div className={classes.roomFrame}>
          <LiveKitRoom
            audio={false}
            video={false}
            token={token}
            data-lk-theme="default"
            style={{ height: '100%' }}
            serverUrl={getConfigOrThrow('livekit-ws-url')}
            onDisconnected={handleDisconnection}
          >
            <StreamDetector
              onStreamStart={handleStreamStart}
              onStreamStop={handleStreamStop}
              sessionStatus={livestreamSessionRecord.data?.status}
            />
            {streamStarted || livestreamStarted ? (
              <ViewerConference defaultWidgetState={{ showChat: true, unreadMessages: 0 }} />
            ) : (
              <div className="flex items-center justify-center h-full bg-gray-100 rounded-lg">
                <div className="text-center">
                  <div className="animate-pulse mb-4">
                    <div className="w-16 h-16 bg-blue-500 rounded-full mx-auto mb-4"></div>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">Waiting for livestream to start...</h3>
                  <p className="text-gray-500">
                    The host hasn't started streaming yet. You'll automatically see the stream when it begins.
                  </p>
                </div>
              </div>
            )}
            <RoomAudioRenderer />
          </LiveKitRoom>
        </div>
        {roomId && <ViewerBreakoutRoomBanner livestreamSessionId={roomId} />}

        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
          <p>
            <strong>Need help?</strong> Having trouble with audio or video? Make sure your browser has provided
            permissions to read audio and video!{' '}
            <a
              href="https://support.google.com/chrome/answer/2693767"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline hover:text-blue-800"
            >
              Browser permissions guide
            </a>{' '}
            or{' '}
            <a
              href="https://support.mozilla.org/en-US/kb/how-manage-your-camera-and-microphone-permissions"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline hover:text-blue-800"
            >
              Microphone troubleshooting
            </a>
            .
          </p>
        </div>
      </div>
    </main>
  );
}
